import { Text } from "@tremor/react";

// ── Encabezado homologado de los 3 tableros ───────────────
// Círculo con el ícono del restaurante a la IZQUIERDA; a la DERECHA, tres
// líneas verticalmente alineadas con jerarquía decreciente:
//   Línea 1: nombre del tablero            (mayor jerarquía)
//   Línea 2: nombre comercial - razón social (jerarquía media)
//   Línea 3: descripción del reporte - periodo filtrado (jerarquía baja)

export interface RestaurantIdentity {
  id: string;
  slug: string;
  name: string;
  razon_social?: string | null;
  logo_url?: string | null;
}

// Color de fondo sólido del círculo por slug (mantiene cada ícono visible:
// el logo de Alado es crema → fondo oscuro; Cosme es negro y La 73 verde → fondo claro).
const CIRCLE_BG: Record<string, string> = {
  alado: "bg-zinc-900",
  cosme: "bg-white border border-gray-300",
  la73: "bg-white border border-gray-300",
};

const CIRCLE_FG: Record<string, string> = {
  alado: "text-zinc-100",
  cosme: "text-zinc-900",
  la73: "text-emerald-800",
};

/** Inicial (fallback) cuando el restaurant no tiene logo. */
function initial(name: string): string {
  return (name.trim()[0] || "?").toUpperCase();
}

export default function DashboardHeader({
  title,
  description,
  restaurant,
  period,
  iconSize = 44,
}: {
  title: string;
  description?: string;
  restaurant: RestaurantIdentity;
  period?: string | null;
  iconSize?: number;
}) {
  const bg = CIRCLE_BG[restaurant.slug] ?? "bg-zinc-700";
  const fg = CIRCLE_FG[restaurant.slug] ?? "text-white";
  const name = restaurant.name;
  const razon = restaurant.razon_social || "";

  // Línea 2: nombre comercial - razón social (omite partes vacías)
  const line2 = [name, razon].filter(Boolean).join(" - ");
  // Línea 3: descripción del reporte - periodo filtrado
  const line3 = [description, period].filter(Boolean).join(" - ");

  return (
    <div className="flex items-center gap-3 min-w-0">
      {/* Círculo con ícono (precede todo el bloque de texto) */}
      <span
        className={`inline-flex items-center justify-center rounded-full overflow-hidden shrink-0 ${bg}`}
        style={{ width: iconSize, height: iconSize }}
        title={name}
        aria-label={`Ícono de ${name}`}
      >
        {restaurant.logo_url ? (
          <img
            src={restaurant.logo_url}
            alt={`Logo de ${name}`}
            className="w-[70%] h-[70%] object-contain"
          />
        ) : (
          <span className={`font-semibold ${fg}`} style={{ fontSize: iconSize * 0.45 }}>
            {initial(name)}
          </span>
        )}
      </span>

      {/* Tres líneas de texto, alineadas a la derecha del ícono */}
      <div className="flex flex-col leading-tight min-w-0">
        {/* Línea 1: nombre del tablero (mayor jerarquía) */}
        <Text className="!text-lg font-semibold !text-tremor-content-emphasis">
          {title}
        </Text>
        {/* Línea 2: nombre comercial - razón social (jerarquía media) */}
        {line2 && (
          <Text className="!text-sm !text-tremor-content">{line2}</Text>
        )}
        {/* Línea 3: descripción - periodo (jerarquía baja) */}
        {line3 && (
          <Text className="!text-xs !text-tremor-content truncate">{line3}</Text>
        )}
      </div>
    </div>
  );
}
